<script lang="ts" setup>
import { Clipboard } from "@capacitor/clipboard";
import { computed, nextTick, onMounted, ref, toRaw, watch } from "vue";
import FileList from "@/components/business/FileList.vue";
import {
  IonContent,
  IonHeader,
  IonPage,
  useIonRouter,
  IonToolbar,
  IonButton,
  IonButtons,
  modalController,
  alertController,
  toastController,
} from "@ionic/vue";
import BackButton from "@/components/ui/BackButton.vue";
import { useRoute } from "vue-router";
import { useEventsStore } from "@/store/events";
import {
  EventType,
  FabriqEvent,
  FabriqEventPayload,
  EventProperty,
  FabriqEventWithReadableProperties,
  User,
  MediaType,
  DisplayableEventTypeProperty,
} from "@/types";
import { localize, getTranslationAndMissingStatus } from "@/utils/localize";
import { useEventTypesStore } from "@/store/eventTypes";
import mixpanelTracker from "@/utils/mixpanel-tracker";
import { useFabriqStore } from "@/store/fabriq";
import EventPropertyField from "@/components/business/events/EventPropertyField.vue";
import EventOptionsModal from "@/components/modals/EventOptionsModal.vue";
import config from "@/config";
import { useEventsWithPropertiesStore } from "@/store/eventsWithProperties";
import { storeToRefs } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { useI18n } from "vue-i18n";
import EventMissingFields from "@/components/business/events/EventMissingFields.vue";
import EventDependencies from "@/components/business/events/EventDependencies.vue";
import { formatDistanceToNow, parseISO } from "date-fns";
import { getDateFnsLocales } from "@/i18n";
import { useClientStore } from "@/store/client";
import { useDiamondDependenciesStore } from "@/store/diamondDependencies";
import { useEventFiles } from "@/composables/useEventsFiles";
import { useAddFileItem } from "@/composables/useAddFileItem";
import { fabriqDeepClone } from "@/services/fabriqDeepClone";
import { parseDateStringWithTimezoneToUTCString } from "@/utils/dates";
import { useEventPermissions } from "@/composables/permissions/useEventPermissions";

const router = useIonRouter();
const fabriqStore = useFabriqStore();
const route = useRoute();
const eventsStore = useEventsStore();
const eventsWithPropertiesStore = useEventsWithPropertiesStore();
const diamondDependenciesStore = useDiamondDependenciesStore();
const clientStore = useClientStore();

const eventTypeStore = useEventTypesStore();
const { t } = useI18n();

const { eventsWithProperties } = storeToRefs(eventsWithPropertiesStore);
const { locale } = fabriqStore;

const { can } = useEventPermissions();

const eventId = ref<string | undefined>(route.params?.id as string);
const creatingEvent = computed(() => !eventId.value);
const eventType = computed<
  (EventType & { isMissingTranslation: boolean }) | undefined
>(() => {
  let displayableEventType: EventType | undefined = undefined;
  if (route.params?.eventTypeId) {
    displayableEventType = eventTypeStore.collection.find(
      (e: EventType) => e.id === route.params?.eventTypeId
    );
  } else if (eventId.value) {
    const event = eventsStore.collection.find(
      (e: FabriqEvent) => e.uuid === eventId.value
    );
    if (!event) {
      return undefined;
    }
    displayableEventType = eventTypeStore.collection.find(
      (e: EventType) => e.id === event.eventTypeId
    );
  }
  if (!displayableEventType) return undefined;
  const { translation: label, missing: isMissingTranslation } =
    getTranslationAndMissingStatus(displayableEventType.label, locale, 0);

  return {
    ...displayableEventType,
    label,
    isMissingTranslation,
  };
});

const canEdit = computed(() => {
  if (!existingEvent.value && creatingEvent.value) {
    return true;
  }
  if (!existingEvent.value) {
    return false;
  }
  return can.event.editEvent(existingEvent.value.id);
});

const existingEvent = computed<FabriqEventWithReadableProperties | undefined>(
  () => {
    return eventsWithProperties.value.find(
      (e: FabriqEvent) => e.uuid === eventId.value
    );
  }
);

const createdSince = computed(() => {
  if (!existingEvent.value) return "";

  return formatDistanceToNow(
    parseISO(new Date(existingEvent.value.createdAt).toISOString()),
    {
      locale: getDateFnsLocales(fabriqStore.locale),
    }
  );
});

const creator = computed(() => {
  if (!existingEvent.value) return "";
  const creator = clientStore.usersWithFutureUserId.find(
    (u: User) => u.future_user_id === existingEvent.value?.createdBy
  );
  if (!creator) return null;
  const fullName = creator?.profile?.full_name
    ? creator.profile.full_name
    : `${creator.first_name} ${creator.last_name}`;
  if (fullName.length > 20) {
    return `${creator.first_name.substring(0, 1).toLocaleUpperCase()}. ${
      creator.last_name
    }`;
  }
  return fullName;
});

const event = ref<FabriqEventPayload & { uuid: string }>({
  uuid: uuidv4(),
  eventTypeId: eventType.value.id,
  properties: [],
  files: [],
});

const { files } = useEventFiles(event);

const sortedEventTypeProperties = computed<DisplayableEventTypeProperty[]>(
  () => {
    if (!eventType.value?.properties) {
      return [];
    }
    const dateProperty = eventType.value.properties.find(
      (p: { type: string }) => p.type === "datetime"
    );
    const zoneProperty = eventType.value.properties.find(
      (p: { type: string }) => p.type === "zone"
    );
    const otherProperties = eventType.value.properties
      .filter(
        (p: { type: string }) => p.type !== "datetime" && p.type !== "zone"
      )
      .map((property) => {
        const { translation: label, missing: isMissingTranslation } =
          getTranslationAndMissingStatus(property.label, locale, 0);
        return { ...property, label, isMissingTranslation };
      });

    const {
      translation: datePropertyLabel,
      missing: datePropertyMissingTranslation,
    } = dateProperty
      ? getTranslationAndMissingStatus(dateProperty.label, locale, 0)
      : { translation: undefined, missing: false };

    const {
      translation: zonePropertyLabel,
      missing: zonePropertyMissingTranslation,
    } = zoneProperty
      ? getTranslationAndMissingStatus(zoneProperty.label, locale, 0)
      : { translation: undefined, missing: false };

    return [
      {
        ...dateProperty,
        label: datePropertyLabel,
        isMissingTranslation: datePropertyMissingTranslation,
        mandatory: true,
      },
      {
        ...zoneProperty,

        label: zonePropertyLabel,
        isMissingTranslation: zonePropertyMissingTranslation,
        mandatory: true,
      },
      ...otherProperties,
    ] as DisplayableEventTypeProperty[];
  }
);

const requiredEventTypeProperties = computed<DisplayableEventTypeProperty[]>(
  () => {
    return sortedEventTypeProperties.value.filter((p) => p.required);
  }
);

const missingFields = computed(() => {
  return requiredEventTypeProperties.value.filter((p: { id: string }) => {
    return !event.value?.properties.find(
      (ep: { eventPropertyTypeId: string }) => ep.eventPropertyTypeId === p.id
    );
  });
});

const nbMissingFields = computed(() => missingFields.value.length);
const nbRequiredFields = computed(
  () => requiredEventTypeProperties.value.length
);

const shownEventTypeProperties = ref<DisplayableEventTypeProperty[]>();

const hiddenEventTypeProperties = ref<DisplayableEventTypeProperty[]>();

const eventChangedFromStore = computed<boolean>(() => {
  if (existingEvent.value) {
    const allPropertyTypeIds = new Set([
      ...existingEvent.value.properties.map((p) => p.eventPropertyTypeId),
      ...event.value.properties.map((p) => p.eventPropertyTypeId),
    ]);
    for (const propTypeId of allPropertyTypeIds) {
      const storeProperty = existingEvent.value.properties.find(
        (p) => p.eventPropertyTypeId === propTypeId
      );
      const currentProperty = event.value.properties.find(
        (p) => p.eventPropertyTypeId === propTypeId
      );
      if (storeProperty?.value !== currentProperty?.value) {
        return true;
      }
    }
    return filesChanged.value;
  }
  return true;
});

const sortedShownEventTypeProperties = computed(() => {
  if (!shownEventTypeProperties.value) return [];
  return [...shownEventTypeProperties.value].sort((a, b) => a.order - b.order);
});

let nbFiles = 0;
onMounted(() => {
  if (existingEvent.value) {
    nbFiles = existingEvent.value.files.reduce((acc, f) => {
      acc = f.id ?? 0 > acc ? f.id ?? 0 : acc;
      return acc;
    }, 0);
    event.value = {
      id: existingEvent.value.id,
      uuid: existingEvent.value.uuid as string,
      eventTypeId: existingEvent.value.eventTypeId,
      properties: existingEvent.value.properties,
      files: existingEvent.value.files ?? [],
    };
  }
  nextTick(() => {
    const toShowProperties: DisplayableEventTypeProperty[] = [];
    const toHideProperties: DisplayableEventTypeProperty[] = [];

    sortedEventTypeProperties.value.forEach((p) => {
      if (!canEdit.value) {
        toShowProperties.push(p);
        return;
      }
      const eventPropertyValue = event.value?.properties.find(
        (ep) =>
          ep.eventPropertyTypeId === p.id &&
          ep.value !== undefined &&
          ep.value !== null &&
          ep.value !== ""
      );
      if (p.required || eventPropertyValue) {
        toShowProperties.push(p);
      } else {
        toHideProperties.push(p);
      }
      if (p.type === "zone" && eventPropertyValue?.value) {
        updateTimezoneFromZonePropertyValue(Number(eventPropertyValue.value));
      }
    });
    shownEventTypeProperties.value = toShowProperties;
    hiddenEventTypeProperties.value = toHideProperties;
  });
});

const alertOnClose = async () => {
  const errorMessages = shownEventTypeProperties.value
    ?.map((p) => validateEventTypeProperty(p.id))
    .filter(Boolean);
  if (errorMessages?.length) {
    const toast = await toastController.create({
      message: `🚨 ${t("eventContent.fieldsContainErrorsMessage")}`,
      duration: 2000,
    });
    toast.present();
    return {
      shouldSave: false,
      shouldDismiss: false,
    };
  }

  if (missingFields.value.length > 0) {
    const mandatoryFieldsMissing = missingFields.value.some((p) => p.mandatory);
    const alertInput: {
      header: string;
      message: string;
      buttons: { text: string; role: string; cssClass?: string }[];
    } = {
      header: "",
      message: "",
      buttons: [],
    };
    if (mandatoryFieldsMissing) {
      const missingFieldLabels = missingFields.value
        .filter((p) => p.mandatory)
        .map((p) => `<strong>${p.label}</strong>`)
        .join(", ");
      alertInput.header = t("eventContent.missingMandatoryFieldsHeader");
      alertInput.message = `${t(
        "eventContent.missingMandatoryFieldsMessage"
      )} ${missingFieldLabels}`;
      alertInput.buttons = [
        {
          text: t("eventContent.close"),
          role: "close",
          cssClass: "alert-modal-nok-small",
        },
        {
          text: t("eventContent.completeEvent"),
          role: "cancel",
          cssClass: "alert-modal-ok-small",
        },
      ];
    } else {
      alertInput.header = t("eventContent.missingRequiredFieldsHeader");
      alertInput.buttons = [
        {
          text: creatingEvent.value
            ? t("eventContent.create")
            : t("eventContent.save"),
          role: "save",
          cssClass: "alert-modal-nok-small",
        },
        {
          text: t("eventContent.completeEvent"),
          role: "cancel",
          cssClass: "alert-modal-ok-small",
        },
      ];
    }

    const missingFieldLabels = missingFields.value
      .filter((p) => p.required && !p.mandatory)
      .map((p) => `<strong>${localize(p.label, locale, 0)}</strong>`)
      .join(", ");

    const previousMessageContent = alertInput.message;

    const messageContent = mandatoryFieldsMissing
      ? "missingRequiredFields"
      : creatingEvent.value
      ? "missingRequiredFieldsWithCreateMessage"
      : "missingRequiredFieldsWithSaveMessage";

    alertInput.message = `${
      mandatoryFieldsMissing ? `<p>${previousMessageContent}</p><p>` : ""
    }
       ${t(`eventContent.${messageContent}`)} ${missingFieldLabels}${
      mandatoryFieldsMissing ? `</p>` : ""
    }`;

    const alert = await alertController.create({
      cssClass: "alert-modal",
      header: alertInput.header,
      message: alertInput.message,
      buttons: alertInput.buttons,
    });
    alert.present();
    const { role } = await alert.onDidDismiss();
    switch (role) {
      case "save": {
        return { shouldSave: true, shouldDismiss: true };
      }
      case "close": {
        return { shouldSave: false, shouldDismiss: true };
      }
      case "cancel":
      case "backdrop": {
        return { shouldSave: false, shouldDismiss: false };
      }
      default: {
        throw new Error("Unknown role");
      }
    }
  }
  return { shouldSave: true, shouldDismiss: true };
};

const dismissEventModal = (action?: string) => {
  if (router.canGoBack()) {
    router.back();
  } else {
    router.push("/main/events");
  }
  mixpanelTracker.track("close | event | event modal", {
    action,
  });
};

const saveEvent = () => {
  const rawEvent = {
    ...fabriqDeepClone(toRaw(event.value)),
    files: event.value.files.map((f) => fabriqDeepClone(toRaw(f))),
    isComplete: nbMissingFields.value === 0,
  };
  if (creatingEvent.value) {
    eventsStore.add(rawEvent);
  } else {
    mixpanelTracker.track("save | event | event modal");
    eventsStore.update(event.value.uuid, rawEvent);
  }
  eventsStore.save();
};

const closeEvent = async () => {
  if (!eventChangedFromStore.value) {
    dismissEventModal("noChanges");
    return;
  }
  const { shouldSave, shouldDismiss } = await alertOnClose();

  if (shouldSave) {
    saveEvent();
  }
  if (shouldDismiss) {
    dismissEventModal(shouldSave ? "savedChanges" : "closedWithoutSaving");
  }
};

watch(nbMissingFields, (value, oldValue) => {
  if (!existingEvent.value) {
    return;
  }
  if (oldValue && value === 0) {
    mixpanelTracker.track("complete event | event | event mobile tab");
  }
});

const showProperty = async (id: string) => {
  const eventPropertyType = sortedEventTypeProperties.value.find(
    (p) => p.id === id
  );
  if (!eventPropertyType) {
    return;
  }
  shownEventTypeProperties.value = [
    ...(shownEventTypeProperties.value ?? []),
    eventPropertyType,
  ];
  hiddenEventTypeProperties.value = hiddenEventTypeProperties.value?.filter(
    (p) => p.id !== id
  );
};

function updateTimezoneFromZonePropertyValue(value: number) {
  const zone = clientStore.allZones.find((z) => z.id === value);
  const zoneSiteId = zone?.groups?.[0];
  const zoneSite = clientStore.groups.find((group) => {
    return group.id === zoneSiteId;
  });
  siteTimezone.value = zoneSite?.timezone ?? phoneTimezone;
}

const phoneTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
const siteTimezone = ref(phoneTimezone);
const updateEvent = (payload: EventProperty[]) => {
  const zonePropertyType = sortedEventTypeProperties.value.find(
    (propertyType) => propertyType.type === "zone"
  );
  const zoneProperty = payload.find(
    (p) => p.eventPropertyTypeId === zonePropertyType?.id
  );

  if (zoneProperty?.value) {
    updateTimezoneFromZonePropertyValue(Number(zoneProperty.value));
  }

  event.value = {
    ...event.value,
    properties: payload,
  };
};
const openEventOptions = async () => {
  const modal = await modalController.create({
    component: EventOptionsModal,
    canDismiss: true,
    mode: "ios",
    breakpoints: [0, 0.3, 0.5],
    initialBreakpoint: 0.3,
    componentProps: {
      ticket: event.value,
      canEdit: canEdit.value,
      onDone: (action: string) => {
        switch (action) {
          case "copy":
            if (!event.value) {
              return;
            }
            mixpanelTracker.track("copy | url event | modal");
            const webAppUrl = fabriqStore.url || config.webappUrl;
            Clipboard.write({
              string: `${webAppUrl}/?event=${event.value.id}`,
            });
            break;
          case "delete":
            mixpanelTracker.track("delete | event | modal");
            if (event.value) {
              eventsStore.remove(event.value);
            }
            router.back();
            eventsStore.save();
            if (event.value?.id) {
              diamondDependenciesStore.removeEventTicketDependencies(
                event.value.id
              );
            }
            break;
        }
        modal.dismiss();
      },
      onCancel: () => modal.dismiss(),
    },
  });
  mixpanelTracker.track("open | event setting | event");
  await modal.present();
  await modal.onDidDismiss();
};

const filesChanged = ref(false);

async function addFile(file: any) {
  if (!event.value) {
    return;
  }
  filesChanged.value = true;
  const id = ++nbFiles;
  if (file.media_type === "url") {
    event.value.files.push({
      id,
      media_type: MediaType.Url,
      _type: MediaType.Url,
      key: `link-${id}`,
      url: file.url,
    });
    mixpanelTracker.track("add link | event | events");
  } else {
    const url = file?._file?.url;
    if (!url) return;
    event.value.files.push({
      id,
      media_type: MediaType.File,
      _type: MediaType.File,
      tmpUrl: url,
      tmpName: file.name,
      url: "",
    });
    mixpanelTracker.track("upload file | event | events");
  }
}

function deleteFile(file: any) {
  filesChanged.value = true;
  event.value.files = event.value.files.filter((f) => f.id !== file.id);
}

const { addFileItem } = useAddFileItem(addFile, "add file | event | event");

// Return either an error message or undefined
function validateEventTypeProperty(
  eventTypePropertyId: string
): string | undefined {
  const eventTypeProperty = sortedEventTypeProperties.value.find(
    (p) => p.id === eventTypePropertyId
  );
  if (!eventTypeProperty) return undefined;

  switch (eventTypeProperty.type) {
    case "datetime":
      if (eventTypeProperty.canBeFuture) return undefined;
      const datetimeProperty = event.value.properties.find(
        (ep) => ep.eventPropertyTypeId === eventTypePropertyId
      );
      if (!datetimeProperty) return undefined;
      const dateString = datetimeProperty.value as string;
      const eventDate = new Date(
        siteTimezone.value
          ? parseDateStringWithTimezoneToUTCString(
              dateString,
              siteTimezone.value
            )
          : dateString
      );
      const nowDate = new Date();

      return eventDate > nowDate ? t("eventContent.futureDateTime") : undefined;
    default:
      return undefined;
  }
}
</script>

<template>
  <ion-page>
    <ion-header mode="ios">
      <ion-toolbar class="ion-padding-start transparent-toolbar">
        <back-button
          slot="start"
          :text="t('common.saveAndExit')"
          @back="closeEvent"
        />
        <ion-buttons slot="end">
          <ion-button
            v-if="event"
            class="toolbar-end-button"
            @click="openEventOptions"
          >
            <font-icon name="more_horiz" size="1.25" material color="primary" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content v-if="eventType" :fullscreen="true" class="event-page">
      <div class="event-content ion-padding-start ion-padding-end">
        <div class="event-type-label">
          {{ eventType?.config?.emoji }}
          {{ eventType.label }}
          <span
            class="warning-missing-translation"
            v-if="eventType.isMissingTranslation"
          >
            ( {{ t("events.missingTranslation") }} )
          </span>
        </div>

        <div class="event-metadata">
          <div class="event-created-by-at">
            {{ createdSince }} ∙ {{ creator }}
          </div>
          <EventMissingFields
            v-if="!existingEvent?.isComplete"
            class="event-type-missing"
            :nbFilled="nbRequiredFields - nbMissingFields"
            :nbTotal="nbRequiredFields"
            :requiredFields="requiredEventTypeProperties"
            :missingFields="missingFields"
            :eventType="eventType"
          />
        </div>

        <EventDependencies
          v-if="existingEvent && !creatingEvent"
          :fEvent="existingEvent"
          :disabled="!canEdit"
        />

        <div
          v-for="eventTypeProperty of sortedShownEventTypeProperties"
          :key="eventTypeProperty.id"
          class="event-type-property"
        >
          <EventPropertyField
            :eventTypeProperty="eventTypeProperty"
            :event="event"
            :eventType="eventType"
            @updateEvent="updateEvent"
            :canEdit="canEdit"
            :siteTimezone="siteTimezone"
            :errorMessage="validateEventTypeProperty(eventTypeProperty.id)"
          />
        </div>

        <div class="hidden-properties">
          <div
            v-for="eventTypeProperty of hiddenEventTypeProperties"
            :key="eventTypeProperty.id"
            class="event-type-property"
          >
            <div
              class="add-property"
              @click="showProperty(eventTypeProperty.id)"
            >
              <font-icon
                name="add"
                size="0.875"
                color="var(--ion-color-secondary)"
                material
              />
              {{ eventTypeProperty.label }}
            </div>
          </div>
        </div>

        <!-- EVENT FILES -->
        <div>
          <file-list
            class="file-list-events"
            :viewer="true"
            :files="files"
            :readonly="!canEdit"
            v-if="files && files.length"
            @delete="deleteFile"
          />
          <div
            class="events-add-file"
            v-if="canEdit"
            :class="{
              'no-file': !files?.length,
            }"
            @click="addFileItem()"
          >
            <font-icon
              name="add"
              size="0.875"
              material
              color="var(--ion-color-secondary)"
            />
            {{ t("tickets.newFile") }}
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
.event-page {
  --background: transparent;
}

.event-page .event-content {
  background: var(--ion-color-primary-contrast);
  border-radius: var(--ion-padding);
  padding-top: calc(var(--ion-padding) * 1);
  min-height: 100%;
}

.event-type-label {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--ion-color-primary);
  margin-top: var(--ion-padding);
  font-size: var(--font-size-xl);
}

.event-missing-fields {
  font-size: var(--font-size-md);
  color: var(--ion-color-danger);
  margin-top: var(--ion-padding);
}

.event-type-property {
  font-size: var(--font-size-md);
  color: var(--ion-color-primary);
  margin-top: var(--ion-padding);
  margin-bottom: var(--ion-padding);
}

.event-type-missing {
  font-size: var(--font-size-md);
  color: var(--ion-color-primary);
  margin-top: var(--ion-padding);
  width: fit-content;
}
.warning-missing-translation {
  color: var(--ion-color-warning);
  font-size: var(--font-size-s);
}
.add-property {
  background-color: var(--ion-background-color-shade);
  border: 1px solid var(--ion-border-color);
  color: var(--ion-color-primary);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  border-radius: 50px;
  padding: calc(var(--ion-padding) / 2) var(--ion-padding);
  font-size: var(--font-size-s);
  margin: 0;
  max-width: 200px;
  width: fit-content;
}

.hidden-properties {
  padding-top: calc(var(--ion-padding) / 2);
}

.event-metadata {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--ion-padding);
  margin-bottom: calc(var(--ion-padding) * 2);
}

.event-created-by-at {
  flex: 1;
  padding-top: calc(2 * var(--ion-padding));
  font-size: var(--font-size-s);
  color: var(--ion-color-primary-shade);
}

.events-add-file {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;
  gap: var(--ion-padding);
  color: var(--ion-color-secondary);
  font-size: var(--font-size-s);
  padding: calc(var(--ion-padding) / 2);
  margin: var(--ion-padding);
  border-radius: var(--f-border-radius);
}

.file-list-events {
  padding-top: 0;
  padding-bottom: 0;
  background: transparent;
}
</style>
